/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState } from "react";
import Paper from "@material-ui/core/Paper";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CheckIcon from "@material-ui/icons/Check";
import Tooltip from "@material-ui/core/Tooltip";
import Avatar from "@material-ui/core/Avatar";
import Icon from "@material-ui/core/Icon";
import Button from "@material-ui/core/Button";
import { jsx } from "@emotion/react";

const ActionItemTypes = {
  ProprietarySpecific: "Proprietary Specific",
  ProprietaryGeneral: "Proprietary General",
  ThirdParty: "Third Party",
  OpenTextQuestion: "Open Text Question",
};

const previewResponseTextarea = {
  width: "100%",
  padding: "12px",
  border: "1px solid #D3D3D3",
  borderRadius: "4px",
  fontFamily: "inherit",
  fontSize: "14px",
};

const root = (isComplete, isLocked) => ({
  display: "flex",
  flexWrap: "wrap",
  background: isComplete && !isLocked ? "var(--Z2)" : "white",
  /* Z18 */
  border: "1px solid #D3D3D3;",
  boxSizing: "border-box;",
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.15);",
  borderRadius: "4px;",
  padding: "24px 24px 24px 32px",
  cursor: isLocked ? "default" : "pointer",
  "&:hover": {
    boxShadow: isComplete || isLocked ? "" : "0px 4px 4px rgba(0, 0, 0, .30)",
  },
  "@media (max-width: 767px)": {
    padding: "15px 16px 21px 16px",
  },
});
const textStatus = {
  marginLeft: "24px",
  marginRight: "80px",
  "& i": {
    verticalAlign: "baseline !important",
  },
  "@media (max-width: 767px)": {
    display: "flex",
    flexDirection: "column",
    marginLeft: "16px",
    marginRight: "40px",
    fontSize: "14px",
  },
};
const expandedChildren = {
  display: "block",
  width: "100%",
};

const checkField = (isComplete, isLocked) => ({
  background: isComplete && !isLocked ? "var(--B1)" : "white",
  border: "1px solid #D3D3D3",
  boxSizing: "border-box",
  borderRadius: "100px",
  width: "30px",
  minWidth: "30px",
  height: "30px",
});

const tooltip = {
  "& .MuiTooltip-tooltip": {
    fontSize: "16px",
    fontFamily: "Adelle Sans W01",
  },
};

const checkIcon = {
  color: "white",
};

const lockIcon = {
  color: "var(--Z4)",
};

const link = (isHovered, isActive) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  marginLeft: "auto",
  whiteSpace: "nowrap",
  fontStyle: "normal",
  fontWeight: "bold",
  fontSize: "13px",
  lineHeight: "14px",
  textTransform: "uppercase",
  color: "var(--B1) !important",
  cursor: !isActive ? "default" : "pointer",
  verticalAlign: "middle",
  WebkitTransform: "perspective(1px) translateZ(0)",
  transform:
    isHovered && isActive
      ? "translateX(8px)"
      : "perspective(1px) translateZ(0)",
  boxShadow: "0 0 1px rgba(0, 0, 0, 0)",
  WebkitTransitionDuration: "0.3s",
  transitionDuration: "0.3s",
  WebkitTransitionProperty: "transform",
  transitionProperty: "transform",
});

const icon = {
  fontSize: "13px",
};

const statusStyle = (displayText) => ({
  display: "inline",
  marginLeft: "8px",
  color: displayText === "Required" ? "var(--Z7)" : "",
  "@media (max-width: 767px)": {
    marginLeft: "0px",
  },
});

const completeButton = (isComplete) => ({
  marginTop: "15px",
  paddingRight: "10px",
  backgroundColor: isComplete ? "#D3D3D3" : "var(--B1)",
  color: isComplete ? "var(--Z4)" : "var(--B2)",
  boxShadow: "none",
  fontFamily: "Adelle Sans W01",
  textTransform: "none",
  "&:active": {
    boxShadow: "none",
  },
  "&:hover": {
    backgroundColor: isComplete ? "#D3D3D3" : "var(--B3)",
  },
});

export const Statuses = {
  Complete: "Complete",
  Locked: "Locked",
  Active: "Incomplete",
};

export const ActionItemCard = ({
  actionItem,
  status,
  setActionItems,
  className = "",
}) => {
  const { actionItemTitle, requirement, isComplete, slug } = actionItem;
  console.log(slug)
  const isLocked = status === Statuses.Locked;
  const isRequired = requirement === "Required";

  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isThirdParty = actionItem.actionItemType === ActionItemTypes.ThirdParty;
  const isOpenTextQuestion =
    actionItem.actionItemType === ActionItemTypes.OpenTextQuestion;
  const isExpandable = isThirdParty || isOpenTextQuestion;

  function markCompleted(ActionItemID) {
    try {
      setActionItems((prev) =>
        prev.map((mappedActionItem) => {
          if (mappedActionItem.actionItemID === ActionItemID) {
            return { ...mappedActionItem, IsComplete: true };
          }
          return mappedActionItem;
        })
      );
    } catch (e) {
      console.error(e);
    }
  }

  const toggle = () => setIsOpen((prevState) => !prevState);
  const navigate = (url) => window.open(url);
  const handleOnClick = () => (isExpandable ? toggle() : navigate(slug));

  return (
    <Paper
      css={root(isComplete, isLocked)}
      className={`${className}`}
      onClick={() => !isLocked && handleOnClick()}
      elevation={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="displayflex aligncenter justifyflexend width100">
        <CheckField
          status={status}
          isComplete={isComplete}
          onMouseEnter={() => setIsHovered(false)}
          onMouseLeave={() => setIsHovered(true)}
        />
        {isMobile ? (
          <div css={textStatus} className="subhead-s">
            <Status isRequired={isRequired} isComplete={isComplete} />
            <span dangerouslySetInnerHTML={{ __html: actionItemTitle }} />
          </div>
        ) : (
          <div css={textStatus} className="subhead-s">
            <span dangerouslySetInnerHTML={{ __html: actionItemTitle }} />
            <Status isRequired={isRequired} isComplete={isComplete} />
          </div>
        )}
        <CTAButtonText
          actionItem={actionItem}
          isOpen={isOpen}
          isHovered={isHovered}
          isLocked={isLocked}
        />
      </div>
      {isExpandable && isOpen && (
        <div css={expandedChildren}>
          <hr />
          {actionItem.actionItemType === ActionItemTypes.ThirdParty ? (
            <>
              <ExpandedCardHeader>How it works</ExpandedCardHeader>
              <ExpandedCardText>
                <span
                  dangerouslySetInnerHTML={{
                    __html: actionItem.thirdPartyHowItWorks,
                  }}
                />
              </ExpandedCardText>
              <ExpandedCardHeader>
                How to complete action item
              </ExpandedCardHeader>
              <ExpandedCardText>
                {actionItem.thirdPartyCompletionMechanism ===
                "Self-reported" ? (
                  "After completing the steps above under “How it Works,” press the button below to let us know that you have completed this action item."
                ) : (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: actionItem.thirdPartyHowToComplete,
                    }}
                  />
                )}
              </ExpandedCardText>
              {actionItem.thirdPartyCompletionMechanism === "Self-reported" && (
                <CompleteButton
                  isComplete={isComplete}
                  onCompleteFn={() => markCompleted(actionItem.actionItemID)}
                />
              )}
            </>
          ) : null}
          {isOpenTextQuestion && (
            <>
              <ExpandedCardHeader>
                <span
                  dangerouslySetInnerHTML={{ __html: actionItem.questionText }}
                />
              </ExpandedCardHeader>
              {actionItem.questionHelpText && (
                <ExpandedCardText>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: actionItem.questionHelpText,
                    }}
                  />
                </ExpandedCardText>
              )}
              <ExpandedCardText>
                <textarea
                  css={previewResponseTextarea}
                  rows={5}
                  disabled
                  placeholder="The learner types their response here."
                />
              </ExpandedCardText>
            </>
          )}
        </div>
      )}
    </Paper>
  );
};

const CTAButtonText = ({ isOpen, isHovered, isLocked, actionItem }) => (
  <>
    {actionItem.actionItemType === ActionItemTypes.ThirdParty ||
    actionItem.actionItemType === ActionItemTypes.OpenTextQuestion ? (
      <ExpandToggle
        isOpen={isOpen}
        isHovered={isHovered}
        isActive={!isLocked}
      />
    ) : (
      <Link isHovered={isHovered} isActive={!isLocked} />
    )}
  </>
);

export const ExpandedCardHeader = ({ children, className = "" }) => (
  <div className={`label-large mb15 mt20 ${className}`}>{children}</div>
);

export const ExpandedCardText = ({ children, className = "" }) => (
  <div className={`secondary-medium ${className}`}>{children}</div>
);

export const CompleteButton = ({ isComplete, onCompleteFn }) => (
  <Button
    css={completeButton(isComplete)}
    onClick={() => onCompleteFn()}
    disabled={isComplete}
  >
    <Icon className="fa fa-check-circle mr10" />{" "}
    {isComplete ? "Completed" : "Complete This Action Item"}
  </Button>
);

const CheckField = ({ status = "Incomplete", isComplete, ...rest }) => {
  const StatusIcon = () => {
    if (status === Statuses.Locked) {
      return <LockIcon css={lockIcon} />;
    } else if (isComplete) {
      return <CheckIcon css={checkIcon} />;
    } else {
      return <></>;
    }
  };
  return (
    <Tooltip title={`Status: ${status}`} css={tooltip} placement="top" arrow>
      <Avatar
        css={checkField(isComplete, status === Statuses.Locked)}
        {...rest}
      >
        <StatusIcon />
      </Avatar>
    </Tooltip>
  );
};

const Link = ({ isHovered, isActive }) => (
  <span css={link(isHovered, isActive)}>
    GO <Icon css={icon} className="fa fa-external-link-square" />
  </span>
);

const Status = ({ isRequired, isComplete }) => {
  const displayText = isComplete ? "Complete" : isRequired ? "Required" : "";
  return (
    <div css={statusStyle(displayText)} className="secondary-small">
      {displayText}
    </div>
  );
};

const LockIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.7">
      <path
        d="M14.2857 7H13.4286V4.75C13.4286 2.13125 10.9929 0 8 0C5.00714 0 2.57143 2.13125 2.57143 4.75V7H1.71429C0.767857 7 0 7.67188 0 8.5V14.5C0 15.3281 0.767857 16 1.71429 16H14.2857C15.2321 16 16 15.3281 16 14.5V8.5C16 7.67188 15.2321 7 14.2857 7ZM10.5714 7H5.42857V4.75C5.42857 3.50938 6.58214 2.5 8 2.5C9.41786 2.5 10.5714 3.50938 10.5714 4.75V7Z"
        fill="#616161"
      />
      <ellipse cx="8.00002" cy="11.2933" rx="1.6" ry="1.46667" fill="#C4C4C4" />
      <rect x="7.67993" y="12.4667" width="0.64" height="1.76" fill="#C4C4C4" />
    </g>
  </svg>
);

const ExpandToggle = ({ isHovered, isActive, isOpen }) => (
  <div css={link(isHovered, isActive)}>
    HOW TO{" "}
    <Icon
      css={icon}
      className={`ml5 fa ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`}
    />
  </div>
);

export default ActionItemCard;
